import { Chip } from "@mui/material";
// import { Box } from "@mui/material"; // ECRH-5397: used by commented-out Provider column
import { GridColDef, GridSortItem } from "@mui/x-data-grid";

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import { BILLING_TABLE_ROW_HEIGHT } from "../../../../../components/app/provider/patient/chart";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  applyDefaultColumnSettings,
  formatMoney,
  getIdFromObj,
  getValueFromObj,
  // ECRH-5397: used by commented-out Payment ID column
  // getFormattedId,
  // hasActiveFilters,
  roleFilter,
  filterActionCol,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import {
  DEFAULT_FILTERS,
  DEFAULT_PATIENT_PAYMENT_TAB,
  PatientPaymentFilters,
  PATIENT_PAYMENT_FILTER_INITIAL_EXPANDED,
  PATIENT_PAYMENT_TABS,
} from "./patient-payments-constant";
import PaymentsTopBar from "./PaymentsTopBar";
import UpsertPatientPayments from "./UpsertPatientPayments";
import ViewPatientPayment from "./ViewPatientPayment";
import ElevatedPaper from "../../../../../components/ElevatedPaper";

import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import { GetPatientAllPaymentPaymentModeEnum } from "../../../../../services";
import UpdatePayment from "./UpdatePayment";
// import Flex from "../../../../../components/Flex"; // ECRH-5397: used by commented-out Provider column
// import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import { Chip, Popover } from "@mui/material";
import DetailsHoverPopOverList from "../../../../../components/DetailsHoverPopOverList";
import { useGetPatientPaymentV2 } from "../../../../../queries/billings/payments/use-get-patient-payment-v2";
import { useRoleController } from "../../../../../components/useRoleController";
import { isEmpty } from "lodash";
import PageLayout from "../../../../../components/PageLayout";

// ECRH-5397: Status column shows only Paid / Partially Paid.
const PAYMENT_STATUS_LABEL: Record<string, string> = {
  PAID: "Paid",
  PARTIALLY_PAID: "Partially Paid",
};

const PAYMENT_STATUS_COLOR: Record<string, { color: string; bgColor: string }> =
  {
    PAID: { color: "#00B917", bgColor: "#E5F8E7" },
    PARTIALLY_PAID: { color: "#FFAA00", bgColor: "#FFF4E4" },
  };

const PatientPayments = () => {
  const [debouceSearchVal, setSearchStringKey, instantSearchVal] =
    useDebounce<string>("");

  const [searchParams, setSearchParams] = useSearchParams();

  const requestedPatientPayment = searchParams.get("activePatientPayment");
  // A hidden tab stays unreachable by bookmarked URL.
  const activePatientPayment =
    PATIENT_PAYMENT_TABS.find((tab) => tab.value === requestedPatientPayment)
      ?.value ?? DEFAULT_PATIENT_PAYMENT_TAB;

  const [activeTab, setActiveTab] = useState(activePatientPayment);
  const isPatientPayment = activeTab === "patient-payment";

  const [pagination, setPagination] = useState(DEF_PAGINATION);
  const [patientPaymentFilter, setPatientPaymentFilter] =
    useState<PatientPaymentFilters>(DEFAULT_FILTERS);

  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  // const [isReferral_Provider_list, setReferral_Provider_list] =
  //   useState<boolean>(false);
  // const { data: providersList } = useProvidersList({
  //   pagination: DEF_PAGINATION,
  //   xTENANTID: getXtenantId(),
  //   isReferral: true,
  // });

  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    PATIENT_PAYMENT_FILTER_INITIAL_EXPANDED
  );
  // console.log("patientPaymentFilter", patientPaymentFilter);
  const { data: patientPayment, isFetching } = useGetPatientPaymentV2({
    pagination: pagination,
    gridSorting: gridSorting,
    patientUuid: patientPaymentFilter.patientName
      ? patientPaymentFilter.patientName.value
      : undefined,
    paymentMethod: patientPaymentFilter.paymentMethod
      ? (patientPaymentFilter.paymentMethod
          .value as GetPatientAllPaymentPaymentModeEnum)
      : undefined,
    typeOfPayment: isPatientPayment ? "PATIENT" : "INSURANCE",
    transactionDate: patientPaymentFilter.transactionDate
      ? new Date(patientPaymentFilter.transactionDate).toISOString()
      : undefined,
    transactionId: patientPaymentFilter.transactionId ?? undefined,
    searchString: debouceSearchVal ?? undefined,
    visitId: patientPaymentFilter.visitId?.value ?? undefined,
    superbillId: patientPaymentFilter.superbillId?.value ?? undefined,
    claimId: patientPaymentFilter.claimId ?? undefined,
  });

  // const [referralId, setReferralId] = useState("");
  // const { data } = useGetReferralById({
  //   referralId: referralId,
  //   xTENANTID: getXtenantId() as string,
  // });

  // const referralData = data?.data;

  const viewPatientPaymentDisclosureHost = useDisclosure();
  const patientPaymentDisclosureHost = useDisclosure(false, {
    onClose: () => {
      // setReferralId("");
    },
  });
  const updatePaymentDisclosureHost = useDisclosure();

  const { hasView: hasViewPayments, hasUpdate: hasUpdatePayments } =
    useRoleController("Apply Payments");
  const {
    hasView: hasViewInsurancePayments,
    hasUpdate: hasUpdateInsurancePayments,
  } = useRoleController("Insurance Payments");

  // ECRH-5397: standardized column sequence — Transaction ID, Payment Mode,
  // Paid Amount, Applied Amount, Unapplied Amount, Visit ID, Invoice ID,
  // Category, Transaction Date, Patient Name (Patient Payment only), Status.
  // Insurance-only columns (Payer Name, Provider, Payment ID, Claim ID) are
  // retained for the Insurance Payment tab and filtered out below.
  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "transactionId",
        flex: 0.4,
        minWidth: 100,
        headerName: "Transaction ID",
        renderCell: ({ row }) => (
          <RenderWithTooltip
            value={row?.transactionId || row?.chequeId || ""}
          />
        ),
      },
      {
        field: "paymentMethod",
        flex: 0.5,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Payment Mode",
        renderCell: ({ row }) => {
          return (
            <Text isTabletext>
              {row.paymentMode === "BANK"
                ? row.bankPaymentMode
                : row.PaymentMode !== "BANK"
                  ? row.paymentMode === "ONLINE"
                    ? "CARD"
                    : row.paymentMode
                  : "-"}
            </Text>
          );
        },
      },
      {
        field: "amount",
        flex: 0.3,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Paid Amount",
        renderCell: ({ row }) => formatMoney(row?.amount),
      },
      {
        field: "appliedAmount",
        flex: 0.2,
        minWidth: 115,
        disableColumnMenu: true,
        headerName: "Applied Amount",
        align: "center",
        renderCell: ({ row }) => formatMoney(row?.appliedAmount),
      },
      {
        field: "unappliedAmount",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Unapplied Amount",
        sortable: false,
        renderCell: ({ row }) => formatMoney(row?.unappliedAmount),
      },
      {
        field: "visitIds",
        flex: 0.3,
        minWidth: 100,
        headerName: "Visit ID",
        renderCell: ({ row }) => {
          const visitIdsObj = row?.visitIds || {};
          const visitIdKeys = Object.keys(visitIdsObj);
          return <DetailsHoverPopOverList data={visitIdKeys} />;
        },
      },
      {
        field: "claimIds",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        headerName:
          activePatientPayment !== "insurance-payment"
            ? "Invoice ID"
            : "Claim ID",
        renderCell: ({ row }) => {
          const claimIdsObj =
            activePatientPayment !== "insurance-payment"
              ? row?.billIds
              : row?.claimIds || {};
          const claimIdKeys = Object.keys(claimIdsObj);
          return <DetailsHoverPopOverList data={claimIdKeys} />;
        },
      },
      {
        field: "paymentCategory",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Category",
        renderCell: ({ row }) => (
          <Text isTabletext>
            {replaceUnderScoreAddSpace(row?.paymentCategory?.toLowerCase()) ||
              "-"}
          </Text>
        ),
      },
      {
        field: "transactionDate",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Transaction Date",
        renderCell: ({ row }) => NonNullableDate(row?.paymentDate),
      },
      {
        field: "patientName",
        flex: 0.6,
        minWidth: 150,
        disableColumnMenu: true,
        headerName: "Patient Name",
        renderCell: ({ row }) => (
          <PatientName
            patientId={getIdFromObj(row?.patient) ?? ""}
            name={row?.patientName ?? ""}
            gender={row?.patientGender ?? ""}
            birthDate={row?.patientDOB ?? ""}
          />
        ),
      },
      {
        field: "status",
        flex: 0.4,
        minWidth: 120,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Status",
        renderCell: ({ row }) => {
          const s = row?.paymentStatus;
          if (!s) return <Text isTabletext>-</Text>;
          const cfg = PAYMENT_STATUS_COLOR[s];
          return (
            <Chip
              size="small"
              label={
                PAYMENT_STATUS_LABEL[s] ??
                replaceUnderScoreAddSpace(s?.toLowerCase())
              }
              sx={{
                color: cfg?.color,
                bgcolor: cfg?.bgColor,
                padding: "4px 2px",
              }}
            />
          );
        },
      },
      {
        field: "payerName",
        flex: 0.6,
        minWidth: 100,
        headerName: "Payer Name",
        renderCell: ({ row }) => {
          return (
            <RenderWithTooltip value={row?.insurancePayer?.payerName ?? ""} />
          );
        },
      },
      // ECRH-5397: Provider and Payment ID removed from the standardized
      // Patient Payment listing (kept for reference / Insurance context).
      // {
      //   field: "providerName",
      //   flex: 0.5,
      //   minWidth: 150,
      //   headerName: "Provider",
      //   sortable: false,
      //   renderCell: ({ row }) => {
      //     return (
      //       <Flex gap={"10px"} sx={{}}>
      //         <Box>
      //           <Text isTabletext marginBottom={"1px"}>
      //             {!isEmpty(row?.billingProvider)
      //               ? `${getValueFromObj(row?.billingProvider)}`
      //               : ""}
      //           </Text>
      //         </Box>
      //       </Flex>
      //     );
      //   },
      // },
      // {
      //   field: "uuid",
      //   flex: 0.4,
      //   minWidth: 50,
      //   disableColumnMenu: true,
      //   headerName: "Payment ID",
      //   renderCell: ({ row }) => (row?.uuid ? getFormattedId(row?.uuid) : "-"),
      // },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={roleFilter({
                list: [
                  { value: "view", label: "View Payment" },
                  // ECRH-5330: Apply Payment disabled
                  // { value: "apply", label: "Apply Payment" },
                ],
                compWith: {
                  // apply:
                  //   activeTab === "patient-payment"
                  //     ? hasUpdatePayments
                  //     : hasUpdateInsurancePayments,
                  view:
                    activeTab === "patient-payment"
                      ? hasViewPayments
                      : hasViewInsurancePayments,
                },
              })}
              onActionClick={(v) => {
                if (v === "view") {
                  viewPatientPaymentDisclosureHost.open(row?.uuid);
                } else if (v === "apply") {
                  // updatePaymentDisclosureHost.open(row?.uuid);
                }
              }}
            />
          );
        },
      },
    ],
    ["patientName", "transactionDate", "amount", "insuranceAmount"]
  );

  // Insurance Payment listing sequence: Visit ID, Claim ID, Payer Name, Patient
  // Name, Provider, Billed Amount, Paid Amount, Transaction Date, Payment Mode,
  // Tx ID/Cheque ID, Applied Amount, Unapplied Amount. Balance and Payment ID
  // are dropped; Applied/Unapplied fall back to 0.00 until posting fills them.
  const INSURANCE_COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "visitIds",
        flex: 0.3,
        minWidth: 100,
        headerName: "Visit ID",
        renderCell: ({ row }) => (
          <DetailsHoverPopOverList data={Object.keys(row?.visitIds || {})} />
        ),
      },
      {
        field: "claimIds",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Claim ID",
        renderCell: ({ row }) => (
          <DetailsHoverPopOverList data={Object.keys(row?.claimIds || {})} />
        ),
      },
      {
        field: "payerName",
        flex: 0.6,
        minWidth: 120,
        headerName: "Payer Name",
        renderCell: ({ row }) => (
          <RenderWithTooltip value={row?.insurancePayer?.payerName ?? ""} />
        ),
      },
      {
        field: "patientName",
        flex: 0.6,
        minWidth: 150,
        disableColumnMenu: true,
        headerName: "Patient Name",
        renderCell: ({ row }) => (
          <PatientName
            patientId={getIdFromObj(row?.patient) ?? ""}
            name={row?.patientName ?? ""}
            gender={row?.patientGender ?? ""}
            birthDate={row?.patientDOB ?? ""}
          />
        ),
      },
      {
        field: "providerName",
        flex: 0.5,
        minWidth: 150,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Provider",
        renderCell: ({ row }) => (
          <RenderWithTooltip
            value={
              !isEmpty(row?.billingProvider)
                ? (getValueFromObj(row?.billingProvider) ?? "")
                : ""
            }
          />
        ),
      },
      {
        field: "billedAmount",
        flex: 0.3,
        minWidth: 110,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Billed Amount",
        renderCell: ({ row }) => formatMoney(row?.billedAmount),
      },
      {
        field: "amount",
        flex: 0.3,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Paid Amount",
        renderCell: ({ row }) => formatMoney(row?.amount),
      },
      {
        field: "transactionDate",
        flex: 0.4,
        minWidth: 100,
        disableColumnMenu: true,
        headerName: "Transaction Date",
        renderCell: ({ row }) => NonNullableDate(row?.paymentDate),
      },
      {
        field: "paymentMethod",
        flex: 0.5,
        minWidth: 100,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Payment Mode",
        renderCell: ({ row }) => (
          <Text isTabletext>
            {row?.paymentMode === "BANK"
              ? (row?.bankPaymentMode ?? "-")
              : row?.paymentMode === "ONLINE"
                ? "CARD"
                : (row?.paymentMode ?? "-")}
          </Text>
        ),
      },
      {
        field: "transactionId",
        flex: 0.4,
        minWidth: 120,
        headerName: "Tx ID/Cheque ID",
        renderCell: ({ row }) => (
          <RenderWithTooltip
            value={row?.transactionId || row?.chequeId || ""}
          />
        ),
      },
      {
        field: "appliedAmount",
        flex: 0.3,
        minWidth: 115,
        disableColumnMenu: true,
        headerName: "Applied Amount",
        renderCell: ({ row }) => formatMoney(row?.appliedAmount),
      },
      {
        field: "unappliedAmount",
        flex: 0.3,
        minWidth: 125,
        disableColumnMenu: true,
        sortable: false,
        headerName: "Unapplied Amount",
        renderCell: ({ row }) => formatMoney(row?.unappliedAmount),
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => (
          <ActionMenu
            options={roleFilter({
              list: [{ value: "view", label: "View Payment" }],
              compWith: { view: hasViewInsurancePayments },
            })}
            onActionClick={(v) => {
              if (v === "view") {
                viewPatientPaymentDisclosureHost.open(row?.uuid);
              }
            }}
          />
        ),
      },
    ],
    ["patientName", "transactionDate", "amount"]
  );

  // Patient Payment tab: standardized columns (drop insurance-only Payer Name).
  // Insurance Payment tab: its own sequence (see INSURANCE_COLUMNS above).
  const dynamicColumns =
    activePatientPayment === "patient-payment"
      ? filterActionCol(
          COLUMNS?.filter((col) => col.field !== "payerName") as GridColDef[],
          !hasUpdatePayments && !hasViewPayments
        )
      : filterActionCol(
          INSURANCE_COLUMNS as GridColDef[],
          !hasUpdateInsurancePayments && !hasViewInsurancePayments
        );

  return (
    <ElevatedPaper>
      <PageLayout
        header={
          <>
            <PaymentsTopBar
              filters={patientPaymentFilter}
              setFilters={setPatientPaymentFilter}
              activePatientPayment={activeTab}
              setActivePatientPayment={setActiveTab}
              setSearchParams={setSearchParams}
              setSearchStringKey={setSearchStringKey}
              searchStringKey={instantSearchVal}
              setExpanded={setExpanded}
              expanded={expanded}
            />
            <UpsertPatientPayments
              title="Update Patient Payment"
              disclosureHost={patientPaymentDisclosureHost}
              prefillData={{}}
            />
            <UpdatePayment
              title="Update Payment"
              disclosureHost={updatePaymentDisclosureHost}
            />
            <ViewPatientPayment
              title="View Payment"
              disclosureHost={viewPatientPaymentDisclosureHost}
            />
          </>
        }
      >
        <CustomDataGridV2
          loading={isFetching}
          key={activePatientPayment}
          rows={patientPayment?.data.content || []}
          rowCount={patientPayment?.data?.page?.totalElements ?? 10}
          getRowId={(row) => row.uuid}
          columns={dynamicColumns}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          sortModel={gridSorting}
          rowHeight={BILLING_TABLE_ROW_HEIGHT}
        />
      </PageLayout>
    </ElevatedPaper>
  );
};

export default PatientPayments;
